import { useState, useCallback, memo, useEffect } from 'react';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Typography,
  FormHelperText,
} from '@mui/material';
import { validateField } from '../../../util/validation';
import useEditableList from '../../../hooks/useEditableList';
import AuthContext from '../../../context/authContext';
import { useContext } from 'react';

const MAX_VISIBLE_VALUES = 3; // if a multi select has more values, only the first MAX_VISIBLE_VALUES are shown

// debounce hook for performance optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const getOptionDisplayLabel = option => option?.displayLabel ?? option?.label ?? '';

const EditableSelect = memo(function EditableSelect({
  value,
  setValue,
  isEditable = true,
  type = 'select',
  options = [],
  placeholder = '',
  showDefaultAsDisplayValue = false,
  isDialog = false,
  required = false,
  showValidation = false,
  schema = null,
  getOptions = null,
  parentItem = null,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [resolvedOptions, setResolvedOptions] = useState(options || []);
  const authCtx = useContext(AuthContext);
  const { fetchOptionsByResourceId } = useEditableList('internal', {}, authCtx.token);

  // debounce value for validation -> improve performance
  const debouncedValue = useDebounce(value, 300);

  useEffect(() => {
    if (showValidation && schema) {
      const validation = validateField(debouncedValue, schema);
      setValidationError(validation.isValid ? null : validation.errors[0]);
    }
  }, [debouncedValue, schema, showValidation]);

  const handleSelectChange = useCallback(
    event => {
      event.stopPropagation();
      const rawValue = type === 'multi-select' ? [event.target.value].flat() : event.target.value;
      // Support custom transform via schema or automatic objectification via api.objectify
      const transformedValue = (() => {
        if (schema?.transformValue) {
          return schema.transformValue(rawValue, options);
        }

        const objectify = schema?.api?.objectify;
        if (objectify && (type === 'multi-select' || type === 'select')) {
          const idProp = objectify.idProp || schema?.api?.idName || 'id';
          const labelProp = objectify.labelProp || 'name';
          const extra =
            typeof objectify.extra === 'object' && objectify.extra !== null ? objectify.extra : {};

          const toObject = val => {
            const found = (options || []).find(opt => opt.value === val);
            const withId = { [idProp]: found ? found.value : val };
            const withLabel = labelProp ? { [labelProp]: found ? found.label : '' } : {};
            return { ...withId, ...withLabel, ...extra };
          };

          if (type === 'multi-select') {
            const array = Array.isArray(rawValue) ? rawValue : [rawValue].filter(Boolean);
            return array.map(toObject);
          }
          return toObject(rawValue);
        }

        return rawValue;
      })();
      setValue(transformedValue);
      // Keep multi-select open so users can select multiple values without reopening.
      if (type !== 'multi-select') {
        setIsOpen(false);
      }
    },
    [setValue, type, schema, options]
  );

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      if (isEditable) {
        setIsEditing(true);
        setIsOpen(true);
      }
    },
    [isEditable]
  );

  const handleBlur = useCallback(() => {
    if (schema) {
      const validation = validateField(value, schema);
      if (!validation.isValid) {
        setValidationError(validation.errors[0]);
        return;
      }
    }

    setIsEditing(false);
    setIsOpen(false);
    setValidationError(null);
  }, [value, schema]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsEditing(false);
  }, []);

  const handleOpen = useCallback(() => {
    if (isEditable) {
      setIsOpen(true);
      setIsEditing(true);
    }
  }, [isEditable]);

  // resolve options when opened or when dependencies change
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      try {
        // 1) explicit getOptions (can be sync or async)
        if (typeof getOptions === 'function') {
          const res = getOptions(parentItem);
          if (res && typeof res.then === 'function') {
            const arr = await res;
            if (!aborted) setResolvedOptions(Array.isArray(arr) ? arr : []);
            return;
          }
          if (!aborted) setResolvedOptions(Array.isArray(res) ? res : []);
          return;
        }

        // 2) dynamic api by id (e.g., product -> pricing)
        const api = schema?.api || {};
        if (api.resourceById && parentItem) {
          const mapped = await fetchOptionsByResourceId(api, parentItem);
          if (!aborted) setResolvedOptions(mapped);
          return;
        }

        // 3) fallback to provided options
        setResolvedOptions(options || []);
      } catch (e) {
        setResolvedOptions(options || []);
      }
    };

    load();
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getOptions,
    JSON.stringify(parentItem),
    JSON.stringify(schema?.api),
    JSON.stringify(options),
  ]);

  if (isEditing || isDialog) {
    return (
      <FormControl
        fullWidth
        error={showValidation && !!validationError}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        <Select
          value={value || (type === 'multi-select' ? [] : '')}
          displayEmpty
          required={required}
          renderValue={selected => {
            // render placeholder if nothing is selected
            if (!selected || (Array.isArray(selected) && selected.length === 0)) {
              return <span style={{ color: '#aaa' }}>{placeholder}</span>;
            }
            // render chips if multi-select
            if (type === 'multi-select') {
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((v, i) => {
                    const option = resolvedOptions.find(opt => opt.value === v);
                    return option ? (
                      <Chip key={`${v}-${i}`} label={getOptionDisplayLabel(option)} size='small' />
                    ) : null;
                  })}
                </Box>
              );
            }
            // render label if single-select
            const option = resolvedOptions.find(opt => opt.value === selected);
            return option ? getOptionDisplayLabel(option) : selected;
          }}
          onChange={handleSelectChange}
          onBlur={handleBlur}
          multiple={type === 'multi-select'}
          autoFocus
          open={isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
        >
          {/* Placeholder as disabled MenuItem for select (not multi-select) */}
          {type !== 'multi-select' && placeholder && (
            <MenuItem value='' disabled hidden>
              <span style={{ color: '#aaa' }}>{placeholder}</span>
            </MenuItem>
          )}
          {resolvedOptions.map((option, index) => (
            <MenuItem
              key={`${option.value}-${index}`}
              value={option.value}
              onClick={e => {
                e.stopPropagation();
              }}
              data-value={option.value}
              sx={{
                py: 1,
                '& .MuiListItemText-root': {
                  my: 0,
                },
                '& .MuiCheckbox-root': {
                  py: 0.5,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.5rem',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {type === 'multi-select' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: '36px',
                    minHeight: '36px',
                    mr: 1,
                  }}
                >
                  <Checkbox
                    checked={value?.includes(option.value)}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.5rem',
                      },
                    }}
                  />
                </Box>
              )}
              <ListItemText
                primary={getOptionDisplayLabel(option)}
                secondary={option?.secondaryLabel || undefined}
                secondaryTypographyProps={{
                  sx: {
                    display: option?.secondaryLabel ? 'block' : 'none',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </MenuItem>
          ))}
        </Select>
        {showValidation && validationError && (
          <FormHelperText sx={{ margin: 0, fontSize: '0.75rem' }}>{validationError}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: isEditable ? 'pointer' : 'default',
        p: 1,
        borderRadius: 1,
        '&:hover': isEditable
          ? {
              bgcolor: 'action.hover',
            }
          : {},
        position: 'relative',
        width: '100%',
        minHeight: '40px',
      }}
    >
      {type === 'multi-select' ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, width: '300px' }}>
          {value && value.length > 0 ? (
            (() => {
              const visibleValues = value.slice(0, MAX_VISIBLE_VALUES);
              const extraCount = value.length - visibleValues.length;
              return (
                <>
                  {visibleValues.map((v, index) => {
                    const option = (resolvedOptions || options || []).find(opt => opt.value === v);
                    return option ? (
                      <Chip key={`${v}-${index}`} label={option.label} size='small' />
                    ) : null;
                  })}
                  {extraCount > 0 && (
                    <Chip
                      key='more-count'
                      label={`+${extraCount} weitere`}
                      size='small'
                      variant='outlined'
                    />
                  )}
                </>
              );
            })()
          ) : showDefaultAsDisplayValue ? (
            <span style={{ color: '#aaa' }}>{placeholder}</span>
          ) : (
            <span style={{ color: '#aaa' }}>{placeholder}</span>
          )}
        </Box>
      ) : (
        // single select
        <Typography sx={{ flex: 1 }}>
          {(() => {
            // Check if value is empty, null, undefined, or doesn't match any option
            if (!value || value === '' || value === null || value === undefined) {
              return '—';
            }
            const option = (resolvedOptions || options || []).find(opt => opt.value === value);
            if (option) return getOptionDisplayLabel(option);
            if (showDefaultAsDisplayValue) return placeholder;
            return '—';
          })()}
        </Typography>
      )}
    </Box>
  );
});

export default EditableSelect;
